(() => {
  function waitForRechargeBundleApi(timeoutMs = 15000) {
    return new Promise((resolve, reject) => {
      const started = Date.now();
      const check = () => {
        const bundleApi = window.recharge?.bundle || window.recharge;
        if (bundleApi?.getDynamicBundleItems) {
          resolve(bundleApi);
          return;
        }
        if (Date.now() - started >= timeoutMs) {
          reject(new Error('Recharge bundle checkout is unavailable. Please refresh and try again.'));
          return;
        }
        window.setTimeout(check, 100);
      };
      check();
    });
  }

  class ClcOffer02 extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.productInfo = this.closest('product-info');
      this.form = this.productInfo?.querySelector('form[data-type="add-to-cart-form"]');

      this.querySelectorAll('.clc-o2__tier').forEach((tier) => {
        tier.addEventListener('click', (event) => {
          if (event.target.closest('.clc-o2__addon, .clc-o2__future-button')) return;
          this.activateTier(tier);
        });
        tier.addEventListener('keydown', (event) => {
          if (event.target !== tier || (event.key !== 'Enter' && event.key !== ' ')) return;
          event.preventDefault();
          this.activateTier(tier);
        });

        const addon = tier.querySelector('.clc-o2__addon');
        addon?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.toggleAddon(tier);
        });
        addon?.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          this.toggleAddon(tier);
        });

        const future = tier.querySelector('.clc-o2__future');
        future?.querySelector('.clc-o2__future-button')?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          future.classList.toggle('is-open');
        });
      });

      const initial =
        this.querySelector(`.clc-o2__tier[data-plan="${this.dataset.defaultPlan}"]`) ||
        this.querySelector('.clc-o2__tier');
      if (initial) this.activateTier(initial);

      this.onSubmit = this.handleSubmit.bind(this);
      this.form?.addEventListener('submit', this.onSubmit, true);
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit, true);
    }

    activateTier(activeTier) {
      this.querySelectorAll('.clc-o2__tier').forEach((tier) => {
        const active = tier === activeTier;
        tier.classList.toggle('is-active', active);
        tier.setAttribute('aria-checked', active ? 'true' : 'false');
        const radio = tier.querySelector('input[type="radio"]');
        if (radio) radio.checked = active;
        if (!active) {
          const addon = tier.querySelector('.clc-o2__addon');
          addon?.classList.remove('is-active');
          addon?.setAttribute('aria-checked', 'false');
          this.paintTier(tier);
        }
      });
      this.paintTier(activeTier);
    }

    toggleAddon(tier) {
      const addon = tier.querySelector('.clc-o2__addon');
      if (!addon) return;
      const active = !addon.classList.contains('is-active');
      addon.classList.toggle('is-active', active);
      addon.setAttribute('aria-checked', active ? 'true' : 'false');
      this.paintTier(tier);
    }

    paintTier(tier) {
      if (!tier) return;
      const addonActive = tier.querySelector('.clc-o2__addon')?.classList.contains('is-active');
      const mode = addonActive ? 'addon' : 'base';
      const values = {
        price: tier.dataset[`${mode}Price`],
        compare: tier.dataset[`${mode}Compare`],
        badge: tier.dataset[`${mode}Badge`],
        charge: tier.dataset[`${mode}Charge`],
        cansCopy: tier.dataset[`${mode}CansCopy`],
        lifetime: tier.dataset[`${mode}Lifetime`],
      };
      const setText = (selector, value) => {
        const element = tier.querySelector(selector);
        if (element && value != null && value !== '') element.textContent = value;
      };
      setText('[data-o2-price]', values.price);
      setText('[data-o2-compare]', values.compare);
      setText('[data-o2-badge]', values.badge);
      setText('[data-o2-charge]', values.charge);
      setText('[data-o2-cans-copy]', values.cansCopy);
      setText('[data-o2-lifetime]', values.lifetime);
      setText('[data-o2-addon-description]', tier.dataset.addonDescription);
      setText('[data-o2-addon-total]', tier.dataset.addonTotal);
    }

    getActiveTier() {
      return this.querySelector('.clc-o2__tier.is-active');
    }

    getActiveFlavor() {
      const button = this.productInfo?.querySelector(
        'clc-custom-variants .clc-option-button.is-active[data-product-id][data-variant-id]'
      );
      if (!button) return null;
      return {
        productId: button.dataset.productId,
        variantId: button.dataset.variantId,
      };
    }

    async handleSubmit(event) {
      const tier = this.getActiveTier();
      if (!tier || !this.form) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const submitButton = this.form.querySelector('[type="submit"]');
      const spinner = submitButton?.querySelector('.loading__spinner');
      submitButton?.setAttribute('aria-disabled', 'true');
      submitButton?.classList.add('loading');
      spinner?.classList.remove('hidden');

      try {
        const flavor = this.getActiveFlavor();
        if (!flavor) throw new Error('Please select an available flavor.');

        const planKey = tier.dataset.plan;
        const sellingPlanId =
          planKey === 'three' ? this.dataset.planThreeId : this.dataset.planOneId;
        if (!sellingPlanId) {
          throw new Error(`Configure the ${planKey === 'three' ? '3 month' : '1 month'} Recharge selling plan ID.`);
        }
        const normalizedSellingPlanId = Number(sellingPlanId);
        if (!Number.isFinite(normalizedSellingPlanId)) {
          throw new Error('The configured Recharge selling plan ID is invalid.');
        }

        const addonActive = tier.querySelector('.clc-o2__addon')?.classList.contains('is-active');
        const quantity = Number(
          addonActive ? tier.dataset.addonQuantity : tier.dataset.baseQuantity
        );
        if (!Number.isFinite(quantity) || quantity <= 0) {
          throw new Error('The selected offer quantity is invalid.');
        }

        const bundleProductId = this.dataset.rcProductId;
        const bundleVariantId = this.dataset.rcVariantId;
        const collectionId = this.dataset.rcCollectionId;
        const bundleHandle = this.dataset.rcHandle;
        if (!bundleProductId || !bundleVariantId || !collectionId || !bundleHandle) {
          throw new Error('Complete the Recharge bundle settings in Customize.');
        }

        let selectedProducts = [flavor];
        if (
          this.dataset.varietyPackProductId &&
          String(flavor.productId) === String(this.dataset.varietyPackProductId)
        ) {
          try {
            selectedProducts = JSON.parse(
              this.querySelector('[data-o2-variety-products]')?.textContent || '[]'
            );
          } catch {
            selectedProducts = [];
          }
          if (!selectedProducts.length) {
            throw new Error('Configure the Variety Pack flavors in Customize.');
          }
        }

        const varietyQuantityKey = `${planKey}${addonActive ? 'Addon' : 'Base'}Quantity`;
        const configuredVarietyTotal = selectedProducts.reduce(
          (sum, selectedProduct) => sum + (Number(selectedProduct[varietyQuantityKey]) || 0),
          0
        );
        const useConfiguredVariety =
          String(flavor.productId) === String(this.dataset.varietyPackProductId) &&
          configuredVarietyTotal > 0;
        if (useConfiguredVariety && configuredVarietyTotal !== quantity) {
          throw new Error(
            `Variety Pack quantities total ${configuredVarietyTotal}; the selected offer requires ${quantity}.`
          );
        }

        const quantityPerProduct = Math.floor(quantity / selectedProducts.length);
        const quantityRemainder = quantity % selectedProducts.length;
        const selections = selectedProducts
          .map((selectedProduct, index) => ({
            collectionId: String(collectionId),
            externalProductId: String(selectedProduct.productId),
            externalVariantId: String(selectedProduct.variantId),
            quantity: useConfiguredVariety
              ? Number(selectedProduct[varietyQuantityKey]) || 0
              : quantityPerProduct + (index < quantityRemainder ? 1 : 0),
            sellingPlan: normalizedSellingPlanId,
          }))
          .filter((selection) => selection.quantity > 0);

        const bundleApi = await waitForRechargeBundleApi();
        const bundle = {
          externalProductId: String(bundleProductId),
          externalVariantId: String(bundleVariantId),
          selections,
        };

        if (typeof bundleApi.validateDynamicBundle === 'function') {
          const validation = await bundleApi.validateDynamicBundle(bundle);
          if (validation !== true) {
            throw new Error(typeof validation === 'string' ? validation : 'Invalid Recharge bundle selection.');
          }
        }

        const items = await bundleApi.getDynamicBundleItems(bundle, bundleHandle);
        if (!Array.isArray(items) || !items.length) {
          throw new Error('Recharge did not return checkout items.');
        }
        let welcomeGifts = [];
        try {
          welcomeGifts = JSON.parse(
            this.querySelector('[data-o2-welcome-gifts]')?.textContent || '[]'
          );
        } catch {
          welcomeGifts = [];
        }
        const cartItems = [
          ...items,
          ...welcomeGifts
            .filter((gift) => gift.id && Number(gift.quantity) > 0)
            .map((gift) => ({ id: String(gift.id), quantity: Number(gift.quantity) })),
        ];

        const root = window.Shopify?.routes?.root || '/';
        const clearResponse = await fetch(`${root}cart/clear.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
        });
        if (!clearResponse.ok) throw new Error('Unable to clear the cart.');

        const addResponse = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items: cartItems }),
        });
        const cartState = await addResponse.json().catch(() => ({}));
        if (!addResponse.ok || cartState.status) {
          throw new Error(cartState.description || 'Unable to add the subscription bundle.');
        }

        const addedWelcomeGifts = cartItems.length > items.length;
        const discountCode = String(this.dataset.welcomeDiscountCode || '').trim();
        const checkoutUrl = window.routes?.checkout_url || `${root}checkout`;
        if (addedWelcomeGifts && discountCode) {
          const separator = checkoutUrl.includes('?') ? '&' : '?';
          window.location.href = `${checkoutUrl}${separator}discount=${encodeURIComponent(discountCode)}`;
          return;
        }

        window.location.href = checkoutUrl;
      } catch (error) {
        console.error('[clc-offer-02]', error);
        window.alert(error.message || 'Unable to create the subscription. Please try again.');
      } finally {
        submitButton?.classList.remove('loading');
        submitButton?.removeAttribute('aria-disabled');
        spinner?.classList.add('hidden');
      }
    }
  }

  if (!customElements.get('clc-offer-02')) {
    customElements.define('clc-offer-02', ClcOffer02);
  }
})();
